import { DeferredPromise, sleep } from '@cf-shared/shared-util';
import { SettingsService } from './settings.service';
import { LoggingService } from './logging.service';
import { untranslatedString } from 'models/translated-string';
import { describeError } from '@cf-shared/error-describe';
import { isCapacitorNative } from 'environments/environment-helpers';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { CameraStrategy, createCameraStrategy } from 'services/camera-strategy';

const CameraPermission = registerPlugin<{ request: () => Promise<void> }>('CameraPermission');

export enum CameraInitResult {
  Success = 'success',
  PermissionDenied = 'permission_denied',
  NoCameraWorked = 'no_camera_worked',
}

export interface CameraInitCallbacks {
  openAsync: (deviceId: string | null) => Promise<void>;
  /** Called between rotation-loop attempts and after terminal failures.
   *  May be async — the rotation loop awaits it before the next attempt, so
   *  async teardown (e.g. Quagga.stop + stream release) completes in-order. */
  cleanupAfterFailure: () => void | Promise<void>;
  retryOnOverconstrained?: (deviceId: string) => Promise<boolean>;
}

export class CameraInfo {
  id: string;
  name: string;
  hasFlash: boolean;
  isSelfie: boolean;
}

/**
 * Shared camera service used by barcode scanner and label scanner pages.
 * Not an Angular @Injectable — each consumer creates its own instance.
 */
export class CameraService {
  cameraInfos: CameraInfo[] = [];
  zoomLevel: number = 0;
  zoomLimits: { min: number, max: number } = undefined;
  flashEnabled = false;
  flashSupported = false;
  cameraPermissionDismissed = false;
  /** Current facing mode — used on native Capacitor to switch front/back via OS */
  activeFacingMode: 'environment' | 'user' = 'environment';
  permissionStatus: { state: 'denied' | 'granted' | 'prompt', addEventListener: (event: string, handler: () => void) => void };

  // pages set this to re-trigger camera init when permission changes to 'granted'
  onPermissionChanged: () => void;

  private permissionReady: DeferredPromise<void>;
  private constraintsApplyCounter = 0;

  /** Lazy-built strategy used by cs-camera-view. Web fallback delegates back
   *  to this service's existing getUserMedia methods. Barcode-scanner callers
   *  don't touch the strategy — they drive CameraService directly. */
  private _strategy: CameraStrategy | null = null;
  get strategy(): CameraStrategy {
    if (!this._strategy) this._strategy = createCameraStrategy(this);
    return this._strategy;
  }

  constructor(
    private settings: SettingsService,
    public readonly logging: LoggingService,
  ) {
    this.permissionReady = DeferredPromise.defer<void>();
    void this.initPermissionAsync();
  }

  private async initPermissionAsync() {
    try {
      this.permissionStatus = await (navigator as any).permissions.query({ name: 'camera' });
      this.permissionStatus.addEventListener('change', () => {
        if (this.permissionStatus.state === 'granted' && this.onPermissionChanged) {
          this.onPermissionChanged();
        }
      });
    } catch {
      // expected, not a failure: this browser's Permissions API does not know 'camera'
      // (every Android WebView, Firefox). Fall back to probing enumerateDevices on
      // visibility change
      this.permissionStatus = { state: 'prompt', addEventListener: () => {} };
      this.installVisibilityFallback();
    }
    this.permissionReady.resolve(null);
  }

  // on Android WebView, the Permissions API doesn't support 'camera', so we can't
  // listen for permission changes — instead, probe enumerateDevices when the app
  // regains visibility (e.g. user returns from Android Settings after granting)
  private installVisibilityFallback() {
    const handler = async () => {
      if (document.visibilityState !== 'visible') return;
      if (!this.onPermissionChanged) return;
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const hasIds = devices.some(d => d.kind === 'videoinput' && d.deviceId?.length > 0);
        if (hasIds) {
          document.removeEventListener('visibilitychange', handler);
          this.permissionStatus = { state: 'granted', addEventListener: () => {} };
          this.onPermissionChanged();
        }
      } catch (err) {
        // the next visibility change probes again, but until one succeeds the screen stays
        // on "permission denied" even if the person has just granted it
        this.logging.trackSilentProblem('CameraPermissionProbeFailed', { error: describeError(err) });
      }
    };
    document.addEventListener('visibilitychange', handler);
  }

  async waitForPermissionAsync(): Promise<void> {
    await this.permissionReady;
  }

  // Requests camera permission and returns a getUserMedia stream.
  // On Android WebView, getUserMedia hangs permanently if it triggers the system
  // permission dialog (WebView bug). The fix: request the Android camera permission
  // natively via our CameraPermission Capacitor plugin BEFORE calling getUserMedia.
  // When getUserMedia fires, permission is already granted, no dialog is shown,
  // and the promise resolves immediately.
  private async requestCameraPermissionAsync(): Promise<MediaStream | null> {
    // on native Android, request permission through our plugin first
    if (isCapacitorNative() && Capacitor.getPlatform() === 'android') {
      try {
        console.log('Requesting camera permission via native plugin');
        await CameraPermission.request();
        console.log('Native camera permission granted');
      } catch {
        // the plugin rejects only when the person refused the permission dialog, and the
        // caller shows the permission screen for that
        console.log('Native camera permission denied');
        return null;
      }
    }

    // now getUserMedia can be called safely — permission is already granted
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: false,
      });
      return stream;
    } catch (err) {
      console.log('getUserMedia failed:', err.name);
      // NotAllowedError is the person refusing, which the permission screen answers. anything
      // else (camera busy, no camera) is a real failure that still shows the permission screen
      if (err?.name !== 'NotAllowedError') {
        this.logging.trackSilentProblem('CameraPermissionProbeGetUserMediaFailed', { errorName: err?.name, error: describeError(err) });
      }
      return null;
    }
  }

  // ── Camera enumeration ──────────────────────────────────────────────

  /**
   * Enumerate all cameras, query their capabilities, and sort by preference.
   *
   * iOS and Android APIs are broken in different ways:
   *  iOS: only enumerates after permission granted, only returns two cameras,
   *       getCapabilities() is on the track not the camera, no zoom/torch.
   *  Android: some devices return multiple back cameras (wide, telephoto),
   *           torch only reported on track level, first camera is usually best.
   */
  async listCamerasAsync(): Promise<void> {
    console.log('Enumerating cameras');

    let devices: MediaDeviceInfo[];
    try {
      devices = await navigator.mediaDevices.enumerateDevices();
    } catch (err) {
      this.logging.trackSilentProblem('CameraEnumerationFailed', { error: describeError(err) });
      return;
    }

    // if devices have no IDs, permission hasn't been granted yet — return empty list
    // (the real camera open will trigger the permission dialog)
    if (!devices.some(d => d.deviceId && d.deviceId.length > 0)) {
      console.log('No device IDs available yet (permission not granted)');
      return;
    }
    console.log('found devices: ', devices);

    const cameras = devices.filter(d => d.kind === 'videoinput') as
      (MediaDeviceInfo & { getCapabilities: () => any })[];

    this.cameraInfos = [];
    for (let c of cameras) {
      try {
        let hasTorch = false;
        let isSelfie = false;
        let hasFacing = false;

        console.log('Checking camera: ', c);
        if (c.getCapabilities) {
          let caps = c.getCapabilities();
          console.log('Camera capabilities: ', caps);
          if (caps.torch) {
            hasTorch = true;
          }
          if (caps.facingMode) {
            hasFacing = true;
            if (caps.facingMode.includes('user')) {
              isSelfie = true;
            }
          }
        }
        // iOS doesn't expose facing mode on the camera level
        if (!hasFacing) {
          isSelfie = c.label && c.label.toLowerCase().includes('front');
        }
        this.cameraInfos.push({ id: c.deviceId, hasFlash: hasTorch, name: c.label, isSelfie: isSelfie });
      } catch (err) {
        console.error('Camera capabilities check failed for camera ', JSON.stringify(c));
        this.logging.trackAbnormalException(err, untranslatedString('Camera capabilities check failed.'));
      }
    }

    // on native Capacitor, don't expose individual camera IDs — use facingMode instead.
    // the OS picks the best multi-lens virtual camera (e.g. Triple on iPhone) automatically.
    // only expose front/back as options so the switch button is a simple toggle.
    if (isCapacitorNative()) {
      const hasBack = this.cameraInfos.some(c => !c.isSelfie);
      const hasFront = this.cameraInfos.some(c => c.isSelfie);
      this.cameraInfos = [];
      if (hasBack) this.cameraInfos.push({ id: 'environment', name: 'Back', hasFlash: false, isSelfie: false });
      if (hasFront) this.cameraInfos.push({ id: 'user', name: 'Front', hasFlash: false, isSelfie: true });
    } else {
      // PWA: sort non-selfie first, then alphabetically
      this.cameraInfos.sort((a, b) => {
        if (!a.isSelfie && b.isSelfie) return -1;
        if (a.isSelfie && !b.isSelfie) return 1;
        if (a.name < b.name) return -1;
        if (a.name > b.name) return 1;
        return 0;
      });
    }

  }

  // ── Camera selection ────────────────────────────────────────────────

  async pickInitialCameraIdAsync(): Promise<string | null> {
    await this.permissionReady;
    // enumerate cameras (will return empty if permission not yet granted)
    if (this.cameraInfos.length === 0) {
      await this.listCamerasAsync();
    }
    // while permission reads 'prompt' the stored id may not belong to this device: iOS Safari
    // changes device ids between sessions, and the setting syncs from other devices. so only
    // trust it if this device lists it. a browser whose Permissions API does not know 'camera'
    // reads 'prompt' for good, and this is what still restores the camera there. without
    // permission nothing is listed, so a device that has not granted access yet still falls
    // through to the permission probe instead of opening a camera id it may not have.
    const storedId = this.settings.barcodeCameraId;
    const storedIdIsUsable = this.permissionStatus.state !== 'prompt'
      || this.cameraInfos.some(c => c.id === storedId);
    if (storedId && storedIdIsUsable) {
      // restore persisted zoom level for barcode scanner
      if (typeof this.settings.barcodeCameraZoom === 'number') {
        this.zoomLevel = this.settings.barcodeCameraZoom;
      }
      return storedId;
    }
    // null when no cameras enumerated yet (first visit, permission not granted)
    return this.cameraInfos[0]?.id || null;
  }

  async pickNextCameraAsync(currentId: string): Promise<string> {
    if (this.cameraInfos.length === 0) {
      await this.listCamerasAsync();
    }
    let currentIndex = this.cameraInfos.findIndex(c => c.id === currentId);
    let nextIndex = (currentIndex + 1) % this.cameraInfos.length;
    return this.cameraInfos[nextIndex] && this.cameraInfos[nextIndex].id;
  }

  // ── Camera open (getUserMedia) ──────────────────────────────────────

  async openCameraAsync(deviceId: string | null, constraints?: MediaTrackConstraints): Promise<MediaStream> {
    const videoConstraints: MediaTrackConstraints = {
      // on native Capacitor, always use facingMode (OS picks the best multi-lens camera)
      // on PWA, fall back to deviceId if available
      ...(isCapacitorNative() || !deviceId
        ? { facingMode: { ideal: this.activeFacingMode } }
        : { deviceId: { exact: deviceId } }),
      ...constraints,
    };
    return navigator.mediaDevices.getUserMedia({ video: videoConstraints, audio: false });
  }

  // ── Track capabilities setup ────────────────────────────────────────

  async setupTrackCapabilitiesAsync(track: MediaStreamTrack): Promise<void> {
    if (!track?.getCapabilities) return;

    let caps = track.getCapabilities() as any;
    console.log('Initialized camera capabilities: ', caps);

    // zoom
    this.zoomLimits = undefined;
    if (caps.zoom && caps.zoom.max && caps.zoom.min) {
      this.zoomLimits = { min: caps.zoom.min, max: caps.zoom.max };
    }
    if (this.zoomLimits) {
      const zoomWorks = await this.testZoomCapabilityAsync(track);
      if (!zoomWorks) {
        console.log('Zoom capability test failed - disabling zoom UI');
        this.zoomLimits = undefined;
      }
    }

    // flash
    this.flashEnabled = false;
    this.flashSupported = caps.torch;

    // default to 1x actual zoom
    if (this.zoomLimits) {
      const range = this.zoomLimits.max - this.zoomLimits.min;
      this.zoomLevel = range > 0 ? Math.max(0, Math.min(1, (1 - this.zoomLimits.min) / range)) : 0;
      await this.adjustZoomAsync(track);
    }
  }

  // ── Track constraints ───────────────────────────────────────────────

  async applyTrackConstraintsAsync(track: MediaStreamTrack, constraints: MediaTrackConstraints): Promise<void> {
    let counter = ++this.constraintsApplyCounter;
    console.log(`Applying track constraints #${counter}:`, JSON.stringify(constraints));
    try {
      await track.applyConstraints(constraints);
      console.log(`Track constraints #${counter} applied successfully.`);
    } catch (err) {
      this.logging.trackSilentProblem(`Camera adjustments #${counter} failed.`, err);
    }
  }

  // test if zoom actually works on this device
  // some Android devices report zoom capability but fail when applying zoom constraints
  // see: https://cybernetic-fitness.sentry.io/issues/6658833461/
  private async testZoomCapabilityAsync(track: MediaStreamTrack): Promise<boolean> {
    if (!this.zoomLimits) return false;
    try {
      await track.applyConstraints({ advanced: [{ zoom: this.zoomLimits.min }] as any });
      return true;
    } catch {
      // expected on the devices this test exists for: false hides the zoom control
      return false;
    }
  }

  // ── Torch / flash ──────────────────────────────────────────────────

  async toggleFlashAsync(track: MediaStreamTrack): Promise<void> {
    this.flashEnabled = !this.flashEnabled;
    if (track) {
      await this.applyTrackConstraintsAsync(track, { advanced: [{ torch: this.flashEnabled } as any] });
    }
  }

  // ── Zoom ────────────────────────────────────────────────────────────

  async adjustZoomAsync(track: MediaStreamTrack): Promise<void> {
    if (this.zoomLimits && track) {
      try {
        const zoomValue = this.zoomLimits.min + (this.zoomLimits.max - this.zoomLimits.min) * this.zoomLevel;
        await track.applyConstraints({ advanced: [{ zoom: zoomValue }] as any });
      } catch (err) {
        // zoom failed although the capability test passed — disable it until the next camera open
        // (setupTrackCapabilitiesAsync resets the limits), so this reports at most once per open
        this.zoomLimits = undefined;
        this.logging.trackSilentProblem('CameraZoomApplyFailed', { error: describeError(err) });
      }
    }
  }

  // ── Post-open camera discovery ─────────────────────────────────────

  // after a successful camera open on first visit (null deviceId), enumerate cameras
  // (permission is now granted) and identify the active camera from the track
  async populateCamerasFromTrackAsync(track: MediaStreamTrack): Promise<string> {
    await this.listCamerasAsync();
    const trackSettings = track.getSettings() as any;
    const resolvedId = trackSettings.deviceId;
    if (resolvedId) {
      this.saveCameraSettings(resolvedId);
    }
    return resolvedId;
  }

  // ── Persistence ─────────────────────────────────────────────────────

  saveCameraSettings(cameraId: string): void {
    this.settings.barcodeCameraId = cameraId;
    this.settings.barcodeCameraZoom = this.zoomLevel;
  }

  saveZoomIfChanged(): boolean {
    if (this.zoomLimits && Math.abs(this.zoomLevel - this.settings.barcodeCameraZoom) > 0.01) {
      this.settings.barcodeCameraZoom = this.zoomLevel;
      return true;
    }
    return false;
  }

  get savedCameraFunctional(): boolean {
    return this.settings.barcodeCameraFunctional;
  }

  set savedCameraFunctional(value: boolean) {
    this.settings.barcodeCameraFunctional = value;
  }

  // ── Camera init orchestration ───────────────────────────────────────

  async initCameraWithRotationAsync(
    callbacks: CameraInitCallbacks,
    options?: { forceCameraId?: string },
  ): Promise<{ result: CameraInitResult, cameraId: string | null }> {
    const forceCameraId = options?.forceCameraId;

    // enumerate cameras if list is empty
    if (this.cameraInfos.length === 0) {
      await this.listCamerasAsync();
    }

    // pick camera: forceCameraId > stored ID > first enumerated > null (first visit)
    let cameraId = forceCameraId || await this.pickInitialCameraIdAsync();

    // if no camera ID (first visit, permission not yet granted), request permission
    // with a direct getUserMedia probe, then open the real camera with the resolved ID.
    // On Android WebView, getUserMedia can hang after the system permission dialog —
    // requestCameraPermissionAsync handles this via visibilitychange-based retry.
    if (!cameraId) {
      console.log('No camera ID available — requesting camera permission');
      const probeStream = await this.requestCameraPermissionAsync();
      if (!probeStream) {
        console.log('Camera permission denied or dismissed.');
        this.cameraPermissionDismissed = true;
        return { result: CameraInitResult.PermissionDenied, cameraId: null };
      }

      // permission granted — get the resolved camera ID from the probe stream
      const probeTrack = probeStream.getVideoTracks()[0];
      const probeDeviceId = (probeTrack?.getSettings() as any)?.deviceId;
      probeStream.getTracks().forEach(t => t.stop());

      // enumerate cameras now that permission is granted
      await this.listCamerasAsync();
      const resolvedId = probeDeviceId || this.cameraInfos[0]?.id;

      // kept for the report below: whether the facing-mode attempt rescues this one decides its tier
      let openByIdError: unknown;
      if (resolvedId) {
        // brief delay for hardware release after stopping the probe stream
        await sleep(300);
        try {
          console.log('Opening camera with resolved ID:', resolvedId);
          await callbacks.openAsync(resolvedId);
          this.saveCameraSettings(resolvedId);
          return { result: CameraInitResult.Success, cameraId: resolvedId };
        } catch (err) {
          console.log('Camera open with resolved ID failed:', err.name);
          openByIdError = err;
          await callbacks.cleanupAfterFailure();
        }
      }

      // fallback: try with facingMode through the callbacks
      try {
        console.log('Fallback: opening with facingMode');
        await callbacks.openAsync(null);
        if (openByIdError) {
          this.logging.trackSilentInfo('CameraOpenByIdFailedFacingModeWorked', { error: describeError(openByIdError) });
        }
        return { result: CameraInitResult.Success, cameraId: null };
      } catch (err) {
        this.logging.trackSilentProblem('CameraOpenAfterPermissionFailed', {
          openByIdError: openByIdError === undefined ? undefined : describeError(openByIdError),
          error: describeError(err),
        });
        await callbacks.cleanupAfterFailure();
        return { result: CameraInitResult.NoCameraWorked, cameraId: null };
      }
    }

    // camera loop: try each camera in rotation
    const startedFromId = cameraId;
    let opened = false;
    do {
      try {
        console.log('Trying camera:', cameraId);
        await callbacks.openAsync(cameraId);
        opened = true;
        break;
      } catch (err) {
        // permission denied — stop immediately
        if (err.name === 'NotAllowedError') {
          console.log('Camera permission denied or dismissed.');
          this.cameraPermissionDismissed = true;
          await callbacks.cleanupAfterFailure();
          return { result: CameraInitResult.PermissionDenied, cameraId };
        }

        // OverconstrainedError: let page retry with relaxed constraints if callback provided
        if (err.name === 'OverconstrainedError') {
          if (callbacks.retryOnOverconstrained) {
            console.log('OverconstrainedError for camera', cameraId, '- trying relaxed constraints');
            await callbacks.cleanupAfterFailure();
            const retryOk = await callbacks.retryOnOverconstrained(cameraId);
            if (retryOk) {
              opened = true;
              break;
            }
            // retry failed — fall through to next camera
          } else {
            console.log('OverconstrainedError for camera', cameraId);
            await callbacks.cleanupAfterFailure();
          }
        } else if (err.name === 'TimeoutError') {
          this.logging.trackSilentProblem('Camera initialization timed out', err);
          await callbacks.cleanupAfterFailure();
        } else if (err.name === 'NotFoundError') {
          console.log('Camera not found:', cameraId);
          await callbacks.cleanupAfterFailure();
        } else if (err.name === 'NotReadableError') {
          this.logging.trackSilentProblem('Camera not readable', err);
          await callbacks.cleanupAfterFailure();
        } else {
          this.logging.trackAbnormalException(err, untranslatedString('Camera selection issue.'));
          await callbacks.cleanupAfterFailure();
        }
      }

      cameraId = await this.pickNextCameraAsync(cameraId);
    } while (startedFromId !== cameraId);

    if (opened) {
      // if we had a specific camera ID, save it. For the null case (first
      // visit, opened via facingMode), the caller resolves the ID post-open
      // by calling populateCamerasFromTrackAsync — see cs-camera-view's
      // openCameraAsync and WebCameraStrategy.startBarcodeDetection.
      if (cameraId) {
        this.saveCameraSettings(cameraId);
      }
      return { result: CameraInitResult.Success, cameraId };
    }

    return { result: CameraInitResult.NoCameraWorked, cameraId: null };
  }
}
